import { useState } from 'react'
import { Card } from '../../components/Card'
import { Button, ErrorBanner, Field, Modal, TextInput } from '../../components/forms'
import { numberOrNull } from '../../lib/forms'
import { formatDateTime, formatMoneyDocument } from '../../lib/format'
import { useCloseSession, useRefreshTill, type RegisterSession } from '../../features/pos/api'

/**
 * The shift so far, and closing it.
 *
 * Expected cash is the server's: float plus cash taken, less change and payouts.
 * It is never typed (M8) — a cashier who can type the expected figure can make any
 * variance zero.
 */
export function RegisterPanel({ session, locationId }: { session: RegisterSession; locationId: string }) {
  const [closing, setClosing] = useState(false)

  return (
    <Card
      title="Register"
      action={
        <Button tone="ghost" onClick={() => setClosing(true)}>
          Close register
        </Button>
      }
    >
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <Stat label="Opened" value={formatDateTime(session.openedAt)} />
        <Stat label="Sales" value={session.salesCount.toLocaleString('en-KE')} />
        <Stat label="Opening float" value={formatMoneyDocument(session.openingFloat)} />
        {session.expectedCash === undefined ? (
          <Stat label="Cash" value="Counted at close" />
        ) : (
          <Stat label="Cash expected" value={formatMoneyDocument(session.expectedCash)} strong />
        )}
        {session.cashSales !== undefined && <Stat label="Cash taken" value={formatMoneyDocument(session.cashSales)} />}
        <Stat label="M-Pesa" value={formatMoneyDocument(session.mpesaSales)} />
        <Stat label="Card and bank" value={formatMoneyDocument(session.cardSales + session.bankSales)} />
        <Stat label="On account" value={formatMoneyDocument(session.creditSales)} />
      </dl>

      {closing && <CloseDialog session={session} locationId={locationId} onClose={() => setClosing(false)} />}
    </Card>
  )
}

function Stat({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-dpl-muted">{label}</dt>
      <dd className={`tabular-nums ${strong ? 'font-semibold text-dpl-text' : 'text-dpl-text'}`}>{value}</dd>
    </div>
  )
}

function CloseDialog({ session, locationId, onClose }: { session: RegisterSession; locationId: string; onClose: () => void }) {
  const close = useCloseSession()
  const refreshTill = useRefreshTill(locationId)
  const [counted, setCounted] = useState('')
  const [note, setNote] = useState('')
  const [result, setResult] = useState<RegisterSession | null>(null)
  const amount = numberOrNull(counted)

  if (result) {
    const variance = result.variance ?? 0
    return (
      <Modal
        open
        title="Register closed"
        onClose={refreshTill}
        // Only now does the till move on to "open the register" — after the
        // variance has been read, not instead of it.
        footer={<Button onClick={refreshTill}>Done</Button>}
      >
        <dl className="space-y-1.5 text-sm">
          <Line label="Expected in the drawer" value={formatMoneyDocument(result.expectedCash ?? 0)} />
          <Line label="Counted" value={formatMoneyDocument(result.countedCash ?? 0)} />
          <Line
            label={variance === 0 ? 'Balanced' : variance < 0 ? 'Short' : 'Over'}
            value={formatMoneyDocument(Math.abs(variance))}
            tone={variance === 0 ? 'text-dpl-success' : 'text-dpl-danger'}
          />
        </dl>
        <p className="mt-3 text-xs text-dpl-muted">The variance is recorded whatever it is. A new session is needed before the next sale.</p>
      </Modal>
    )
  }

  return (
    <Modal
      open
      title="Close the register"
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            busy={close.isPending}
            disabled={amount === null || amount < 0}
            onClick={() => amount !== null && close.mutate({ id: session.id, countedCash: amount, note: note || undefined }, { onSuccess: setResult })}
          >
            Close register
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-dpl-text">
        Count the cash in the drawer and enter the total. The system compares it with what should be there — the expected figure is
        shown only after you have counted, so the count is a count.
      </p>
      <div className="grid gap-4">
        <Field label="Cash counted (KSh)" htmlFor="counted-cash">
          <TextInput id="counted-cash" inputMode="decimal" autoFocus value={counted} onChange={(e) => setCounted(e.target.value)} />
        </Field>
        <Field label="Note" htmlFor="close-note" hint="Anything that explains a difference.">
          <TextInput id="close-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <ErrorBanner error={close.error} />
      </div>
    </Modal>
  )
}

function Line({ label, value, tone = 'text-dpl-text' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-dpl-muted">{label}</dt>
      <dd className={`font-medium tabular-nums ${tone}`}>{value}</dd>
    </div>
  )
}
